import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../hooks/useToast';
import BoxtalShippingProviderPanel from './BoxtalShippingProviderPanel';
import ExistingCarrierAdminPanel from './ExistingCarrierAdminPanel';
import Toast from './Toast';

const SOLUTION_OPTIONS = [
  { value: 'BOXTAL', label: 'Boxtal' },
  { value: 'EXISTING_CARRIER', label: 'Contrat transporteur existant' },
];

/**
 * Super Admin — choix solution transport + bloc provider (B2.1b).
 */
export default function ShippingIntegrationPanel({ commercantId, toast: toastFromParent }) {
  const { toast: localToast, toasts, removeToast } = useToast();
  const toast = toastFromParent ?? localToast;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shippingSolution, setShippingSolution] = useState('BOXTAL');
  const [existingCarrierName, setExistingCarrierName] = useState('');
  const [existingCarrierIntegrationStatus, setExistingCarrierIntegrationStatus] = useState('TO_REVIEW');
  const [draftSolution, setDraftSolution] = useState('BOXTAL');
  const [draftCarrierName, setDraftCarrierName] = useState('');
  const [draftStatus, setDraftStatus] = useState('TO_REVIEW');

  const loadIntegration = useCallback(async () => {
    if (!commercantId) return;
    try {
      setLoading(true);
      const data = await apiService.getShippingIntegration(commercantId);
      const integration = data?.integration || {};
      const solution = integration.shippingSolution || 'BOXTAL';
      setShippingSolution(solution);
      setDraftSolution(solution);
      const name = integration.existingCarrierName || '';
      setExistingCarrierName(name);
      setDraftCarrierName(name);
      const status = integration.existingCarrierIntegrationStatus || 'TO_REVIEW';
      setExistingCarrierIntegrationStatus(status);
      setDraftStatus(status);
    } catch (err) {
      toast.error(err?.message || 'Impossible de charger l’intégration transporteur');
    } finally {
      setLoading(false);
    }
  }, [commercantId, toast]);

  useEffect(() => {
    loadIntegration();
  }, [loadIntegration]);

  const handleSave = async () => {
    if (!commercantId) return;
    if (draftSolution === 'EXISTING_CARRIER' && !String(draftCarrierName || '').trim()) {
      toast.error('Le nom du transporteur est obligatoire.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        shippingSolution: draftSolution,
      };
      if (draftSolution === 'EXISTING_CARRIER') {
        body.existingCarrierName = draftCarrierName.trim();
        body.existingCarrierIntegrationStatus = draftStatus || 'TO_REVIEW';
      }
      const data = await apiService.putShippingIntegration(commercantId, body);
      const integration = data?.integration || {};
      setShippingSolution(integration.shippingSolution || draftSolution);
      setDraftSolution(integration.shippingSolution || draftSolution);
      setExistingCarrierName(integration.existingCarrierName || '');
      setDraftCarrierName(integration.existingCarrierName || '');
      const st = integration.existingCarrierIntegrationStatus || 'TO_REVIEW';
      setExistingCarrierIntegrationStatus(st);
      setDraftStatus(st);
      toast.success('Solution de transport enregistrée');
    } catch (err) {
      toast.error(err?.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const solutionDirty =
    draftSolution !== shippingSolution
    || (draftSolution === 'EXISTING_CARRIER'
      && (draftCarrierName !== (existingCarrierName || '')
        || draftStatus !== (existingCarrierIntegrationStatus || 'TO_REVIEW')));

  if (loading) {
    return (
      <div className="mb-6 flex items-center gap-2 text-gray-600">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        Chargement intégration transporteur…
      </div>
    );
  }

  const showBoxtalPanel = shippingSolution === 'BOXTAL' && draftSolution === 'BOXTAL';

  return (
    <section className="mb-8 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900">Intégration transporteur</h3>
      <p className="mt-1 text-sm text-gray-600">
        Configuration plateforme (Super Admin). Le commerçant ne choisit pas le provider technique.
      </p>

      <div className="mt-4 max-w-md">
        <label htmlFor="shipping-solution-select" className="mb-1 block text-sm font-medium text-gray-800">
          Solution utilisée
        </label>
        <select
          id="shipping-solution-select"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={draftSolution}
          onChange={(e) => setDraftSolution(e.target.value)}
          disabled={saving}
        >
          {SOLUTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {draftSolution === 'EXISTING_CARRIER' && (
        <ExistingCarrierAdminPanel
          carrierName={draftCarrierName}
          integrationStatus={draftStatus}
          onCarrierNameChange={setDraftCarrierName}
          onIntegrationStatusChange={setDraftStatus}
          disabled={saving}
        />
      )}

      <div className="mt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || (draftSolution === 'EXISTING_CARRIER' && !String(draftCarrierName || '').trim())}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer la solution
        </button>
      </div>

      {showBoxtalPanel && (
        <div className="mt-6 border-t border-gray-100 pt-6">
          <BoxtalShippingProviderPanel commercantId={commercantId} />
        </div>
      )}

      {!toastFromParent && toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}
    </section>
  );
}
